import React, { Component, ErrorInfo, ReactNode } from 'react';
import { ActionButton } from 'src/tef/components/buttons/ActionButton';
import { AlertDisplay } from 'src/tef/components/feedback/AlertDisplay';
import { ContainerBox } from 'src/tef/components/primitives/ContainerBox';
import { TypographyText } from 'src/tef/components/primitives/TypographyText';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ContainerBox className="min-h-screen flex items-center justify-center p-4">
          <ContainerBox className="max-w-md w-full">
            <AlertDisplay type="error">
              <TypographyText variant={{ type: 'h3', size: 'lg' }} className="mb-2">
                Something went wrong
              </TypographyText>
              <TypographyText variant={{ size: 'sm' }}>
                {this.state.error?.message || 'An unexpected error occurred'}
              </TypographyText>
            </AlertDisplay>
            <ContainerBox className="mt-4 flex justify-end">
              <ActionButton variant={{ style: 'primary', size: 'md' }} onClick={this.handleReset}>
                Try Again
              </ActionButton>
            </ContainerBox>
          </ContainerBox>
        </ContainerBox>
      );
    }

    return this.props.children;
  }
}
